import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ChevronLeft, ChevronRight, MapPin, Camera } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND}${u}` : u);

export default function Gallery() {
  const { lang } = useApp();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const trackRef = useRef(null);

  const t = {
    title: lang === "id" ? "Dokumentasi Bantuan" : "Aid Documentation",
    subtitle:
      lang === "id"
        ? "Bukti langsung dari lapangan — foto distribusi, lokasi, dan barang yang sudah tersalur."
        : "Direct proof from the field — distribution photos, locations, and items delivered.",
    empty: lang === "id" ? "Belum ada dokumentasi." : "No documentation yet.",
    prev: lang === "id" ? "Sebelumnya" : "Previous",
    next: lang === "id" ? "Berikutnya" : "Next",
  };

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  const scrollTo = (i) => {
    const n = Math.max(0, Math.min(items.length - 1, i));
    setIdx(n);
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[n];
    if (child) track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: "smooth" });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const w = track.clientWidth;
    const near = Math.round(track.scrollLeft / w);
    if (near !== idx) setIdx(near);
  };

  return (
    <section className="mb-12" data-testid="gallery-section">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 flat-border rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] mb-2">
            <Camera className="w-3 h-3" strokeWidth={2} /> Dokumentasi
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight">{t.title}</h2>
          <p className="text-xs sm:text-sm text-[#141414]/70 mt-1 max-w-2xl">{t.subtitle}</p>
        </div>
        <div className="text-[11px] font-mono text-[#141414]/60" data-testid="gallery-count">
          {idx + 1} / {items.length}
        </div>
      </div>

      <div className="relative flat-border rounded-2xl bg-white overflow-hidden">
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: "none" }}
          data-testid="gallery-track"
        >
          {items.map((g, i) => (
            <figure
              key={g.id}
              data-testid={`gallery-slide-${g.id}`}
              className="snap-center shrink-0 w-full relative"
            >
              <img
                src={absUrl(g.image_url)}
                alt={g.caption_id || g.caption_en || "Documentation"}
                className="w-full h-[240px] sm:h-[340px] md:h-[420px] object-cover"
                loading="lazy"
              />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white">
                {g.location && (
                  <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] mb-1.5 bg-white/15 backdrop-blur-sm rounded-full px-2 py-0.5">
                    <MapPin className="w-3 h-3" strokeWidth={2} />
                    {g.location}
                  </div>
                )}
                <div className="text-sm md:text-base font-semibold leading-snug">
                  {(lang === "id" ? g.caption_id : g.caption_en) || g.caption_id || g.caption_en || ""}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => scrollTo(idx - 1)}
              disabled={idx === 0}
              data-testid="gallery-prev"
              aria-label={t.prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white flat-border flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollTo(idx + 1)}
              disabled={idx === items.length - 1}
              data-testid="gallery-next"
              aria-label={t.next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white flat-border flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>

            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollTo(i)}
                  data-testid={`gallery-dot-${i}`}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/85"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
